/**
 * ReturnToOneStop — the "Back to One Stop" strip on a journey page that was
 * launched FROM a One Stop page (KP, 2026-09-17).
 *
 * WHEN IT SHOWS. Only when the URL carries `from=onestop`. A journey opened from
 * a link, a QR code, a kiosk or a search result carries no such parameter and
 * renders nothing here — the same published page serves both arrivals, and this
 * is how it tells them apart. The One Stop hand-off adds `return=<its own URL>`
 * and `fromLabel=<its page name>` alongside; both are optional.
 *
 * WHERE IT SITS. A slim full-width strip ABOVE the journey card — above the
 * brand header and the progress indicator — on every page of the journey,
 * including the confirmation. Outside the brand-themed card on purpose: it is
 * "you came from somewhere", not a step control, and it must never compete
 * with the step's own primary button. Sticky, so it survives a long slot
 * picker or form on a phone. 44px on touch, 40px otherwise.
 *
 * WHAT IT DOES. The journey tab was opened by script from the One Stop tab, so
 * the browser lets this page close ITSELF (`window.close()` is permitted for a
 * script-opened browsing context regardless of the severed opener) — the
 * student lands back on the One Stop tab with the board exactly as they left
 * it, no tab switching. When the browser refuses (a direct link, a kiosk, a
 * same-tab hand-off), the same control navigates to the `return` URL instead.
 * Registered clean-ups (an unfinished appointment hold) run first, so leaving
 * mid-flow does not lock a slot for five minutes.
 *
 * COLOUR. Both controls here are One Stop's own navigation rather than the
 * journey's, so both follow the One Stop theme token and never the journey's
 * brand colour: the strip is a 6% tint of the theme colour over white, a
 * hairline in the surface border, body-colour text and a theme-colour chevron,
 * with the same colour a little more strongly for its "Done" state and its
 * leave dialog; `OneStopDoneButton` is a solid theme-colour button. This
 * governs these two controls only: the journey's own elements keep painting
 * whatever brand the journey carries, which may be the platform default brand
 * rather than the tenant theme.
 *
 * SECURITY. `return` is navigated to only when it is an absolute `https:` URL
 * with no embedded credentials AND it is same-site with the journey's own host
 * (same registrable domain) or names an origin the served configuration
 * allowlists — it arrives on the wire and goes into `location.assign`, so
 * `javascript:` and `data:` must never reach it, and any-https would have made
 * "Back to …" an open redirect under the tenant's name (docket 2026-09-18 item
 * 5). The label is rendered as text, never as markup.
 *
 * FRAMED. When the journey is running inside the One Stop page's own popup
 * (`window.parent !== window`), nothing here navigates: the strip POSTS to the
 * parent and the parent closes the popup (docket 2026-09-18 item 2). Navigating
 * would load the One Stop page inside the frame — One Stop nested in itself.
 */
import React from "react";

export const ONE_STOP_RETURN_PARAMS = {
  source: "from",
  sourceValue: "onestop",
  returnUrl: "return",
  fromLabel: "fromLabel",
} as const;

export interface OneStopReturn {
  readonly returnUrl: string | null;
  readonly fromLabel: string;
}

/* ── where `return` is allowed to point ─────────────────────────────── */

/**
 * The tenant's One Stop origin, delivered in the DOCUMENT — the same channel and
 * shape as `manage-base-url` in the CTA layer. The portal and the journeys ship
 * ONE tenant-agnostic bundle, so a build-time value cannot serve this check.
 *
 * ⛔ NOTHING STAMPS THIS META TODAY, and nothing calls the registrar below
 * either (docket 2026-09-18b item 4): the platform deferred the per-request
 * stamp on their side, so the same-site test is currently the ONLY live rule.
 * This comment said the asset worker stamped it; it does not, and saying so was
 * the difference between a rule an operator could rely on and one they could
 * not. When the stamp lands, reword this — do not reword it before.
 *
 * ABSENT is not "allow anything": the same-site test below is then the only
 * rule, which is the honest state for a stock deployment whose One Stop page
 * and journeys sit on the tenant's own domain.
 */
const ONESTOP_ORIGIN_META_NAME = "onestop-base-url";

const registeredOrigins = new Set<string>();

/**
 * Origins the served tenant configuration names as its own One Stop surfaces.
 * Called by a host that has already read the config (the journey pages do, for
 * their own hostnames); additive, so several callers cannot clobber each other.
 */
export function registerOneStopReturnOrigins(origins: readonly string[]): void {
  for (const raw of origins) {
    try {
      const url = new URL(raw);
      if (url.protocol === "https:") registeredOrigins.add(url.origin);
    } catch { /* not an origin; ignored rather than trusted */ }
  }
}

/** Read LAZILY, never memoized — a memo would freeze whatever the first call saw. */
function metaOnestopOrigin(): string {
  if (typeof document === "undefined" || typeof document.querySelector !== "function") return "";
  const raw = document.querySelector(`meta[name="${ONESTOP_ORIGIN_META_NAME}"]`)?.getAttribute("content")?.trim() ?? "";
  try {
    return raw ? new URL(raw).origin : "";
  } catch {
    return "";
  }
}

/**
 * Registrable-domain comparison WITHOUT a public-suffix list, which no browser
 * bundle should carry for four lines of policy. `a.b.example.com` and
 * `c.example.com` agree on `example.com`; the short list covers the two-label
 * public suffixes a tenant is realistically deployed under, so `x.co.uk` and
 * `y.co.uk` are NOT treated as one site.
 */
const TWO_LABEL_SUFFIXES: ReadonlySet<string> = new Set([
  "co.uk", "org.uk", "ac.uk", "gov.uk", "me.uk", "co.jp", "ne.jp", "or.jp",
  "com.au", "net.au", "org.au", "edu.au", "co.nz", "com.br", "com.mx",
  "co.za", "co.in", "com.sg", "com.hk", "com.tr",
]);

function registrableDomain(host: string): string {
  const labels = host.toLowerCase().replace(/\.$/, "").split(".");
  if (labels.length <= 2) return labels.join(".");
  const lastTwo = labels.slice(-2).join(".");
  return TWO_LABEL_SUFFIXES.has(lastTwo) ? labels.slice(-3).join(".") : lastTwo;
}

/**
 * The host WITHOUT its surface prefix — `onestop.acme.example.com` and
 * `join.acme.example.com` both answer `acme.example.com`.
 *
 * Registrable domain alone is too coarse on a SHARED PLATFORM APEX, where every
 * tenant sits under one root: `join.a.jrni.ai` and `onestop.b.jrni.ai` agree on
 * `jrni.ai`, so tenant A's journey would have accepted a return into tenant B's
 * portal (docket 2026-09-18b item 4a). Both hosts follow
 * `<prefix>.<tenant>.<root>`, so comparing everything past the first label is
 * what actually says "the same tenant" — and on a two-label host there is no
 * prefix to drop, so the host is its own site key.
 */
function siteKey(host: string): string {
  const clean = host.toLowerCase().replace(/\.$/, "");
  // A host that IS its registrable domain has no surface prefix to drop:
  // `acme.co.uk` must stay `acme.co.uk`, not become `co.uk`, or a portal on the
  // bare domain refuses its own `book.acme.co.uk` (platform review, 2026-09-19).
  if (clean === registrableDomain(clean)) return clean;
  const labels = clean.split(".");
  return labels.length > 2 ? labels.slice(1).join(".") : clean;
}

/**
 * `null` unless the URL is somewhere this page may send the visitor: absolute
 * `https:`, no embedded credentials, and EITHER same-site with the journey's own
 * host or an origin the deployment names. Anything else answers `null` and the
 * strip behaves exactly as it does with no `return` at all — it still offers to
 * close the tab, it simply has nowhere to navigate.
 */
function safeReturnUrl(raw: string | null): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password) return null;
  const allowed = [metaOnestopOrigin(), ...registeredOrigins].filter(Boolean);
  if (allowed.indexOf(url.origin) > -1) return url.toString();
  const self = typeof window !== "undefined" ? window.location.hostname : "";
  if (!self) return null;
  if (url.hostname.toLowerCase() === self.toLowerCase()) return url.toString();
  if (registrableDomain(url.hostname) !== registrableDomain(self)) return null;
  return siteKey(url.hostname) === siteKey(self) ? url.toString() : null;
}

/** `null` unless this page was reached from a One Stop page. */
export function readOneStopReturn(search?: string): OneStopReturn | null {
  if (typeof window === "undefined" && search === undefined) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search !== undefined ? search : window.location.search);
  } catch {
    return null;
  }
  if (params.get(ONE_STOP_RETURN_PARAMS.source) !== ONE_STOP_RETURN_PARAMS.sourceValue) return null;
  const label = (params.get(ONE_STOP_RETURN_PARAMS.fromLabel) || "").trim().slice(0, 60);
  return { returnUrl: safeReturnUrl(params.get(ONE_STOP_RETURN_PARAMS.returnUrl)), fromLabel: label };
}

/* ── framed: the popup on the One Stop page owns the way back ───────────── */

/** The envelope every message in both directions carries. */
export const JOURNEY_MESSAGE_SOURCE = "jrni-journey";

export type JourneyMessage =
  | {
      readonly source: typeof JOURNEY_MESSAGE_SOURCE;
      readonly type: "complete";
      readonly complete: boolean;
      /** See `setJourneyResumeUrl`. Absent means "nothing to come back to". */
      readonly resume?: string | null;
    }
  | {
      readonly source: typeof JOURNEY_MESSAGE_SOURCE;
      readonly type: "return";
      readonly resume?: string | null;
    }
  | {
      /** Escape pressed inside the framed journey (docket 2026-09-28 item 2). The
       *  parent treats it as its own close: warns if unfinished, else closes. */
      readonly source: typeof JOURNEY_MESSAGE_SOURCE;
      readonly type: "dismiss";
      readonly resume?: string | null;
    };

/** The one thing the PARENT says to a framed journey (docket 2026-09-18b item 2). */
export type ParentMessage = {
  readonly source: typeof JOURNEY_MESSAGE_SOURCE;
  readonly type: "closing";
};

/**
 * TRUE when this journey is running inside another document's frame — which, on
 * a `from=onestop` arrival, means the One Stop page's popup.
 *
 * A cross-origin parent makes the comparison itself throw in some engines;
 * throwing means there IS a parent, so the catch answers true.
 */
export function isFramedJourney(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

/**
 * ⛔ NEVER `"*"`. The target origin is the origin of the `return` parameter — the
 * One Stop page that opened this journey, already checked by `safeReturnUrl` —
 * so a journey framed by anyone else posts nothing at all rather than
 * broadcasting its state to whoever is listening.
 */
function parentOrigin(ret: OneStopReturn | null): string | null {
  if (!ret || !ret.returnUrl) return null;
  try {
    return new URL(ret.returnUrl).origin;
  } catch {
    return null;
  }
}

function postToParent(message: JourneyMessage, ret?: OneStopReturn | null): boolean {
  if (!isFramedJourney()) return false;
  const origin = parentOrigin(ret === undefined ? readOneStopReturn() : ret);
  if (!origin) return false;
  try {
    window.parent.postMessage({ ...message, resume: resumeUrl }, origin);
    return true;
  } catch {
    return false;
  }
}

/* ── the handle a closed popup would otherwise lose ───────────────────────── */

/**
 * WHERE THIS JOURNEY CAN BE PICKED UP AGAIN (docket 2026-09-18b item 8).
 *
 * A walk-in keeps its live ticket's handle in ITS OWN URL. Framed, that URL is
 * the iframe's — so when the popup closed, the queue/entry pair went with it and
 * the visitor lost position polling AND "Leave the queue" while still standing
 * in the line. In the old same-tab flow the top-level URL carried them and a
 * reload brought the ticket back.
 *
 * So the journey states, whenever it holds something live, the URL that resumes
 * it; every message up carries the current value and the One Stop page keeps it.
 * `null` clears it — the visitor left the queue, or the entry is gone.
 *
 * It is the same-origin journey URL and nothing else: it goes to the popup's
 * pinned origin only, and the parent frames it back at that same origin.
 */
let resumeUrl: string | null = null;

export function setJourneyResumeUrl(url: string | null): void {
  const next = url && typeof url === "string" ? url : null;
  if (next === resumeUrl) return;
  resumeUrl = next;
  // Tell the parent NOW rather than at the next completion change: the popup
  // can be closed a second after the ticket appears.
  postToParent({ source: JOURNEY_MESSAGE_SOURCE, type: "complete", complete });
}

export const journeyResumeUrl = (): string | null => resumeUrl;

/* ── clean-ups a journey wants run before the student leaves mid-flow ─────── */

type Cleanup = () => Promise<unknown> | unknown;
const cleanups = new Set<Cleanup>();

/**
 * A journey that holds something the student would otherwise leave behind (an
 * appointment hold) registers a release here; the strip runs every registered
 * clean-up before closing or navigating. Returns the unregister function — call
 * it when the hold is spent or released by the flow itself.
 */
export function registerReturnCleanup(fn: Cleanup): () => void {
  cleanups.add(fn);
  return () => { cleanups.delete(fn); };
}

async function runCleanups(): Promise<void> {
  const pending = [...cleanups];
  cleanups.clear();
  await Promise.allSettled(pending.map((fn) => Promise.resolve().then(fn)));
}

/* ── is the booking complete? ────────────────────────────────────────────────────── */

/**
 * KP, 2026-09-17: leaving before the booking is complete loses it, so the strip
 * WARNS first. The journey reports where it stands — `setJourneyComplete(true)`
 * on its confirmation / ticket screen, `false` on every other step. Unknown
 * (nothing reported yet) counts as NOT complete: a warning the student can wave
 * through is cheaper than a lost booking.
 */
let complete = false;
const completeListeners = new Set<(v: boolean) => void>();
export function setJourneyComplete(v: boolean): void {
  if (complete === v) return;
  complete = v;
  completeListeners.forEach((fn) => fn(v));
  // Framed, the POPUP is the thing that has to warn before it closes, and it
  // cannot read this module across origins. Every change is posted up.
  postToParent({ source: JOURNEY_MESSAGE_SOURCE, type: "complete", complete: v });
}
export const isJourneyComplete = (): boolean => complete;
function useJourneyComplete(): boolean {
  const [v, setV] = React.useState(complete);
  React.useEffect(() => { completeListeners.add(setV); return () => { completeListeners.delete(setV); }; }, []);
  return v;
}

/**
 * Leave the journey and return to One Stop. Exported so a journey's own
 * confirmation "Done" can do exactly what the strip does — and the in-card Done
 * buttons on all three journeys now call it (docket 2026-09-18 item 3).
 */
/**
 * THE PARENT SAYING IT IS CLOSING (docket 2026-09-18b item 2).
 *
 * The popup's X, its overlay click and "Close without booking" used to just
 * unmount the frame, so any clean-up registered above could not run on a
 * popup-side close. The parent now says so first and waits a moment; this runs
 * them. ⚠ PLUMBING ONLY TODAY (platform review, 2026-09-19): nothing in this app
 * calls `registerReturnCleanup`, so the set is empty and no hold is released by
 * this path — the docket's premise that the old strip released an appointment
 * hold was wrong. When a journey does register a release it must use a
 * `keepalive` request: the frame is unmounted ~150 ms after this message.
 * Same origin discipline as everything posted upward: the message
 * must come from the window that framed this one, at the origin of the `return`
 * parameter.
 */
function bindParentClosing(ret: OneStopReturn | null): (() => void) | undefined {
  if (!isFramedJourney()) return undefined;
  const origin = parentOrigin(ret);
  if (!origin || typeof window === "undefined") return undefined;
  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== origin) return;
    try {
      if (event.source !== window.parent) return;
    } catch {
      return;
    }
    const data = event.data as { source?: unknown; type?: unknown } | null;
    if (!data || typeof data !== "object") return;
    if (data.source !== JOURNEY_MESSAGE_SOURCE || data.type !== "closing") return;
    void runCleanups();
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

export async function returnToOneStop(ret: OneStopReturn): Promise<void> {
  await runCleanups();
  /* FRAMED: ask the parent to close the popup, and do NOT navigate (docket
     2026-09-18 item 2). `window.close()` is a no-op inside a frame, so the
     navigation fallback below would have loaded the One Stop page INSIDE the
     frame — One Stop nested in itself. If the post cannot be addressed (no
     trusted `return`, so no origin to send to) nothing happens here: the popup's
     own close control is still the way out. */
  if (isFramedJourney()) {
    postToParent({ source: JOURNEY_MESSAGE_SOURCE, type: "return" }, ret);
    return;
  }
  // Closing is permitted for a script-opened tab; it is a no-op otherwise.
  try { window.close(); } catch { /* not closable */ }
  // Still here → the browser declined. Navigate instead, when we have a safe URL.
  window.setTimeout(() => {
    if (ret.returnUrl) window.location.assign(ret.returnUrl);
  }, 60);
}

/* ── the in-card Done button ──────────────────────────────────────────────── */

/**
 * "Done — back to <where>" INSIDE the confirmation card (docket 2026-09-18
 * item 3).
 *
 * The strip at the top of the page already changes its label when the flow
 * finishes, but a small link in the top bar was the only way back from a
 * finished booking. This is the primary control on the last step, in the card,
 * where the visitor is looking — and it does exactly what the strip does,
 * through the same `returnToOneStop`, so framed and standalone behave alike.
 *
 * RENDERS NOTHING ON A DIRECT VISIT. The condition is the strip's own: the URL
 * has to say the visitor came from a One Stop page. A journey opened from a QR
 * code has nowhere to go "back" to, and must not be offered one.
 */
export function OneStopDoneButton({ testid, label }: {
  readonly testid?: string;
  /** Overrides the composed "Done — back to X"; the journeys use the default. */
  readonly label?: string;
}): React.JSX.Element | null {
  const ret = React.useMemo(() => readOneStopReturn(), []);
  const [busy, setBusy] = React.useState(false);
  if (!ret) return null;
  /* NOTHING TO GO BACK TO, SO NO BUTTON (docket 2026-09-18b item 4b). Without a
     trusted `return` there is no origin to post to when framed and no URL to
     navigate to when not, so this rendered a primary button that did nothing at
     all — the one failure a confirmation step cannot afford. The strip above
     still shows: it offers to close the tab, which is a weaker promise honestly
     made. A split custom domain reaches this until the deployment names its
     One Stop origin. */
  if (!ret.returnUrl) return null;
  const brand = "var(--jrni-color-primary-1, #4F00F2)";
  const where = ret.fromLabel || "One Stop";
  return (
    <div style={{ display: "flex", paddingTop: "4px" }}>
      <button
        type="button"
        data-testid={testid || "onestop-done"}
        disabled={busy}
        onClick={() => { setBusy(true); void returnToOneStop(ret).finally(() => setBusy(false)); }}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          minHeight: 44, padding: "0 22px", border: 0, borderRadius: 8,
          background: brand, color: "#FFFFFF",
          fontFamily: "inherit", fontSize: 15, fontWeight: 700, lineHeight: 1,
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "Returning…" : (label || `Done — back to ${where}`)}
      </button>
    </div>
  );
}

/* ── the strip ────────────────────────────────────────────────────────────── */

export function ReturnToOneStopBar({ done, testid, preview }: {
  /** True on a confirmation page: the flow is complete, the control reads "Done". */
  readonly done?: boolean;
  readonly testid?: string;
  /** The editor's own preview: draws the strip for this label without reading the
   *  URL, and the control is inert. Never set on a public page. */
  readonly preview?: { readonly fromLabel: string } | null;
}): React.JSX.Element | null {
  const fromUrl = React.useMemo(() => readOneStopReturn(), []);
  const ret: OneStopReturn | null = preview ? { returnUrl: null, fromLabel: preview.fromLabel } : fromUrl;
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const finished = useJourneyComplete() || !!done;
  /* FRAMED: say hello (docket 2026-09-18 item 2). `setJourneyComplete` posts on
     CHANGE, and a journey that opens on its first step never changes anything —
     so the popup would learn nothing until the visitor finished. One post on
     mount gives it the current state, and doubles as the only reliable "the
     framed document is alive and talking" signal it has. */
  React.useEffect(() => {
    if (preview) return undefined;
    postToParent({ source: JOURNEY_MESSAGE_SOURCE, type: "complete", complete: isJourneyComplete() });
    return bindParentClosing(fromUrl);
  }, [preview, fromUrl]);
  /* ESCAPE WHEN FRAMED (docket 2026-09-28 item 2). Focus inside the journey means
     the popup's own Escape handler never sees the key — it is another origin's
     document — so this document says it upward. Bubble phase on `document`, so a
     popover or dialog inside the journey that handles Escape (preventDefault) runs
     first and wins. This strip's own leave dialog closes on Escape rather than
     stacking the parent's guard over it. */
  const confirmingRef = React.useRef(false);
  confirmingRef.current = confirming;
  React.useEffect(() => {
    if (preview || !fromUrl || !isFramedJourney()) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (confirmingRef.current) { event.preventDefault(); setConfirming(false); return; }
      postToParent({ source: JOURNEY_MESSAGE_SOURCE, type: "dismiss" }, fromUrl);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [preview, fromUrl]);
  if (!ret) return null;
  const brand = "var(--jrni-color-primary-1, #4F00F2)";
  const where = ret.fromLabel || "One Stop";
  const label = finished ? `Done — back to ${where}` : `Back to ${where}`;
  const leave = () => {
    setConfirming(false);
    setBusy(true);
    void returnToOneStop(ret).finally(() => setBusy(false));
  };
  const go = () => {
    if (busy || preview) return;
    // Not finished → warn first; the booking is lost on the way out.
    if (!finished) { setConfirming(true); return; }
    leave();
  };
  /* FRAMED IN ONE STOP'S OWN DIALOG, NO STRIP (CC, 29 Sept). The popup already has
     its close control, so "Back to OneStop" inside it pointed at the page the visitor
     is still on. The hooks above still run: the hello post and Escape stay wired. */
  if (!preview && isFramedJourney()) return null;
  return (
    <div
      data-testid={testid || "return-to-onestop"}
      data-onestop-return=""
      style={{
        position: preview ? "relative" : "sticky", top: 0, zIndex: 30,
        display: "flex", alignItems: "center", gap: 12,
        // ALIGNED TO THE JOURNEY'S CONTENT EDGE, not to a centred column. All three
        // hosts (appointment, event, walk-in) draw their body text 37px from the
        // left at every width: a 12px inset around the card (the Designer's
        // scroller on appointment and event, the page wrapper on walk-in) + 1px
        // card border + 24px body padding. The old rule centred the strip on an
        // assumed 1120px column the journeys never had, so the link drifted with
        // the viewport.
        //
        // It is the LABEL that lines up, not the chevron. 35px used to put the
        // arrow's ink on 37 and left the words at 54, which reads as a misaligned
        // row against every line beneath it - the operator caught it on the live
        // page. 18px + the button's 2px + the chevron's (16 - 5) box + the 6px gap
        // puts the text on 37 and hangs the arrow in the gutter, which is what a
        // back control is supposed to look like.
        //
        // The editor's own preview sits over a mock header whose text is 16px in,
        // not over a real journey, so it keeps the narrow inset (14 + 2).
        minHeight: 40, padding: preview ? "0 12px 0 14px" : "0 12px 0 18px",
        boxSizing: "border-box",
        background: `color-mix(in srgb, ${brand} 6%, var(--jrni-color-base-white, #FFFFFF))`,
        borderBottom: "1px solid var(--jrni-color-surface-border, #E6E8EC)",
        fontFamily: "inherit",
      }}
    >
      <button
        type="button"
        onClick={go}
        disabled={busy}
        aria-label={label}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          minHeight: 40, padding: "0 6px 0 2px", margin: 0,
          border: 0, borderRadius: 8, background: "transparent",
          color: finished ? brand : "var(--jrni-color-text-body, #333333)",
          fontFamily: "inherit", fontSize: 13, fontWeight: finished ? 700 : 600, lineHeight: 1,
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* The chevron is drawn 6px inside its 16px box (the path starts at x=9 of
            24), less half its stroke: the INK begins 5.27px in. Aligning the box
            to the content edge left the arrow visibly indented, so the box is
            pulled back by that much and the ink - what the eye lines up - lands
            on the edge. */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginLeft: -5 }}>
          <path d="M15 6l-6 6 6 6" />
        </svg>
        <span>{busy ? "Returning…" : label}</span>
      </button>
      {confirming ? (
        <div role="presentation" onClick={() => setConfirming(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(15,16,22,.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="onestop-leave-title" aria-describedby="onestop-leave-body" onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 400, background: "var(--jrni-color-base-white, #FFFFFF)", borderRadius: 12, boxShadow: "0 18px 48px rgba(15,16,22,.28)", padding: "20px 20px 16px", boxSizing: "border-box", fontFamily: "inherit" }}>
            <div id="onestop-leave-title" style={{ fontSize: 16, fontWeight: 700, color: "var(--jrni-color-text-strong, #1B1D24)", lineHeight: 1.3 }}>Your booking isn’t finished</div>
            <div id="onestop-leave-body" style={{ fontSize: 13.5, color: "var(--jrni-color-text-body, #333333)", lineHeight: 1.5, marginTop: 8 }}>
              If you go back to {where} now, what you’ve entered here is lost and nothing is booked. You can come back and start again.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 18 }}>
              <button type="button" autoFocus onClick={() => setConfirming(false)}
                style={{ flex: "1 1 0", minHeight: 40, padding: "0 16px", borderRadius: 8, border: 0, background: brand, color: "#FFFFFF", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                Keep booking
              </button>
              <button type="button" onClick={leave}
                style={{ flex: "1 1 0", minHeight: 40, padding: "0 16px", borderRadius: 8, border: "1px solid var(--jrni-color-surface-border, #D1D4DC)", background: "transparent", color: "var(--jrni-color-text-body, #333333)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                Leave without booking
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
